import { makeStyles } from "@material-ui/core/styles";
import AppHeader from "@saleor/components/AppHeader";
import { CardSpacer } from "@saleor/components/CardSpacer";
import { Container } from "@saleor/components/Container";
import Grid from "@saleor/components/Grid";
import PageHeader from "@saleor/components/PageHeader";
import SaveButtonBar from "@saleor/components/SaveButtonBar";
import Skeleton from "@saleor/components/Skeleton";
import { OrderDetailsListColumns } from "@saleor/config";
import useListSettings from "@saleor/hooks/useListSettings";
import useNotifier from "@saleor/hooks/useNotifier";
import { sectionNames } from "@saleor/intl";
import { maybe } from "@saleor/misc";
import { orderImageIniData } from "@saleor/motherOrders/utils/initialData";
import { StoreOrderUrlDialog } from "@saleor/storeOrders/urls";
import { ListProps, ListViews } from "@saleor/types";
import {
  DeliveryTypeEnum,
  SmopOrderStatusEnum,
  SmopUserTypeCode
} from "@saleor/types/globalTypes";
import { hasPermission } from "@saleor/utils/handlers/OrderFulfillmentHandlers";
import React from "react";
import { useIntl } from "react-intl";

import { StoreOrderDetails_storeOrder } from "../../types/StoreOrderDetails";
import StoreOrderAssignedRider from "../StoreOrderAssignedRider";
import StoreOrderAssignedRunner from "../StoreOrderAssignedRunner";
import StoreOrderCustomer from "../StoreOrderCustomer";
import StoreOrderDetailsList from "../StoreOrderDetailsList";
import StoreOrderHistory from "../StoreOrderHistory";
import StoreOrderInfo from "../StoreOrderInfo";
import StoreOrderParcelID from "../StoreOrderParcelID/StoreOrderParcelID";
import StoreOrderPayment from "../StoreOrderPayment";
import StoreOrderReminderCard from "../StoreOrderReminderCard";
import StoreOrderSMACCard from "../StoreOrderSMACCard";
import StoreOrderStatus from "../StoreOrderStatus";
import {
  checkAction,
  handleCancelModal,
  handleLabelButton,
  hideSaveButton
} from "./handlers";

const useStyles = makeStyles(
  theme => ({
    date: {
      marginBottom: theme.spacing(3)
    },
    grid: {
      paddingTop: "20px"
    },
    header: {
      display: "flex",
      marginBottom: 0
    },
    headerHolder: {
      display: "inline-flex"
    },
    statusHolder: {
      display: "flex",
      marginLeft: "10px"
    }
  }),
  {
    name: "OrderDetailsPage"
  }
);

export interface StoreOrderDetailsPageProps
  extends ListProps<OrderDetailsListColumns> {
  overrideStatusDialog: () => void;
  reverseCancel: () => void;
  onSubmitOrderLineStatus: any;
  onSubmitOpsScan: (data: any) => void;
  onSubmitRunnerScan: (data: any) => void;
  onSubmitParcelNo: (data: any) => void;
  assignRunnerDialog: any;
  editProductDialog: any;
  userType: SmopUserTypeCode;
  ConfirmDialog: (data: SmopOrderStatusEnum) => void;
  addProductDialog: (data: any) => void;
  onModal: (path: StoreOrderUrlDialog) => void;
  orderStatus?: string;
  storeOrder: StoreOrderDetails_storeOrder;
  disabled: boolean;
  disableAction: boolean;
  enableManageMallActions: boolean;
  enableViewMasterFulfillment: boolean;
  onBack();
}

const StoreOrderDetailsPage: React.FC<StoreOrderDetailsPageProps> = props => {
  const {
    onBack,
    onModal,
    disabled,
    storeOrder,
    ConfirmDialog,
    orderStatus,
    addProductDialog,
    userType,
    assignRunnerDialog,
    editProductDialog,
    onSubmitParcelNo,
    onSubmitRunnerScan,
    onSubmitOpsScan,
    onSubmitOrderLineStatus,
    overrideStatusDialog,
    reverseCancel,
    disableAction,
    enableManageMallActions,
    enableViewMasterFulfillment,
    ...listProps
  } = props;
  const classes = useStyles(props);

  const intl = useIntl();

  const { settings } = useListSettings<OrderDetailsListColumns>(
    ListViews.ORDER_DETAILS_LIST
  );
  const { STORE_ADMIN, OMI_OPS, RUNNER } = SmopUserTypeCode;
  const {
    READY,
    COLLECTED,
    RECEIVED_AT_FC,
    PACKED,
    READY_TO_SHIP,
    SHIPPED,
    COMPLETED,
    CANCELED
  } = SmopOrderStatusEnum;
  const {
    EXPRESS,
    EXPRESS_LONG_DISTANCE,
    IN_STORE_PICKUP,
    SAME_DAY
  } = DeliveryTypeEnum;
  const order = storeOrder?.order;
  const shippingMethod = maybe(() => order.shippingMethod.deliveryType);

  const notify = useNotifier();

  const isMultiTenant = maybe(() => order.isMultiTenant);
  const [showTooltip, setShowTooltip] = React.useState(false);

  React.useEffect(() => {
    setShowTooltip(
      storeOrder?.smopStatus === SmopOrderStatusEnum.READY &&
        shippingMethod === DeliveryTypeEnum.IN_STORE_PICKUP
    );
  }, [storeOrder?.smopStatus, shippingMethod]);

  const riderCardStatus = [
    READY,
    COLLECTED,
    RECEIVED_AT_FC,
    PACKED,
    READY_TO_SHIP,
    SHIPPED,
    COMPLETED,
    CANCELED
  ];

  const showRiderCard = hasPermission(
    { deliveryType: shippingMethod, status: storeOrder?.smopStatus, userType },
    [EXPRESS, EXPRESS_LONG_DISTANCE, SAME_DAY],
    riderCardStatus,
    [STORE_ADMIN, RUNNER, OMI_OPS],
    disabled
  );

  const handleCheckAction = () => {
    checkAction(
      userType,
      storeOrder.smopStatus,
      smopStatus => ConfirmDialog(smopStatus),
      () => overrideStatusDialog(),
      () => reverseCancel(),
      enableManageMallActions
    );
  };

  const handleCancel = () => {
    handleCancelModal(
      userType,
      shippingMethod,
      storeOrder?.smopStatus,
      isMultiTenant,
      enableManageMallActions,
      message => notify(message),
      param => onModal(param)
    );
  };

  const handleHideSaveButton = () =>
    hideSaveButton(
      userType,
      storeOrder?.smopStatus,
      shippingMethod,
      storeOrder?.order?.payments[0]?.paymentMethodType,
      enableManageMallActions
    );

  return (
    <Container>
      <AppHeader onBack={onBack}>
        {intl.formatMessage(sectionNames.storeOrder)}
      </AppHeader>
      <div className={classes.headerHolder}>
        <PageHeader
          className={classes.header}
          inline
          title={storeOrder?.orderCode ?? " "}
        />
        <span className={classes.statusHolder}>
          {storeOrder?.smopStatus ? (
            <StoreOrderStatus status={storeOrder?.smopStatus ?? " "} />
          ) : (
            <Skeleton />
          )}
        </span>
      </div>
      <div>{storeOrder?.tenant?.storeName}</div>
      {userType === STORE_ADMIN ? <StoreOrderReminderCard /> : null}
      <Grid className={classes.grid}>
        <div>
          <StoreOrderDetailsList
            storeOrder={storeOrder}
            settings={settings}
            disabled={false}
            {...listProps}
            orderStatus={orderStatus}
            addProductDialog={addProductDialog}
            editProductDialog={editProductDialog}
            userType={userType}
            onSubmitOrderLineStatus={onSubmitOrderLineStatus}
            disableAction={disableAction}
          />
          <CardSpacer />

          <StoreOrderPayment
            storeOrder={storeOrder}
            onCancel={handleCancel}
            orderStatus={orderStatus}
            disableAction={disableAction}
            enableManageMallActions={enableManageMallActions}
            orderInfoComponent={<StoreOrderInfo storeOrder={storeOrder} />}
          />

          <CardSpacer />
          <StoreOrderHistory
            events={storeOrder?.events}
            images={maybe(() => storeOrder.order.images, orderImageIniData)}
            deliveryType={maybe(
              () => storeOrder.order.shippingMethod.deliveryType
            )}
          />
        </div>
        <div>
          {(userType && userType === OMI_OPS) || enableViewMasterFulfillment ? (
            <div>
              {/* Assigned Runner */}
              {shippingMethod !== IN_STORE_PICKUP && (
                <div>
                  <StoreOrderAssignedRunner
                    runnerData={storeOrder?.runner}
                    homeLocation={storeOrder?.order?.dropOffLocation}
                    assignRunnerDialog={assignRunnerDialog}
                    userType={userType}
                    status={storeOrder?.smopStatus}
                    deliveryType={
                      storeOrder?.order?.shippingMethod?.deliveryType
                    }
                    loading={disabled}
                    disableAction={disableAction}
                    binCode={storeOrder?.dropBinCode}
                  />
                  <CardSpacer />
                </div>
              )}

              {/* Assigned Rider */}
              {showRiderCard && (
                <>
                  <StoreOrderAssignedRider
                    riderData={storeOrder?.order?.rider}
                  />
                  <CardSpacer />
                </>
              )}

              {/* Associated Parcel ID */}
              <StoreOrderParcelID
                storeOrder={storeOrder}
                userType={userType}
                onSubmitParcelNo={onSubmitParcelNo}
                onSubmitRunnerScan={onSubmitRunnerScan}
                onSubmitOpsScan={onSubmitOpsScan}
                disableAction={disableAction}
                enableManageMallActions={enableManageMallActions}
              />
              <CardSpacer />

              {/* Customer */}
              <StoreOrderCustomer storeOrder={storeOrder} userType={userType} />
              <CardSpacer />
            </div>
          ) : (
            <div>
              {userType && userType === STORE_ADMIN && (
                <div>
                  <StoreOrderSMACCard storeOrder={storeOrder} />
                  <CardSpacer />
                </div>
              )}

              {/* Customer */}
              <StoreOrderCustomer storeOrder={storeOrder} userType={userType} />
              <CardSpacer />

              {/* Assigned Runner */}
              {shippingMethod !== IN_STORE_PICKUP && (
                <div>
                  <StoreOrderAssignedRunner
                    runnerData={storeOrder?.runner}
                    homeLocation={storeOrder?.order?.dropOffLocation}
                    assignRunnerDialog={assignRunnerDialog}
                    userType={userType}
                    status={storeOrder?.smopStatus}
                    deliveryType={
                      storeOrder?.order?.shippingMethod?.deliveryType
                    }
                    loading={disabled}
                    disableAction={disableAction}
                    binCode={storeOrder?.dropBinCode}
                  />
                  <CardSpacer />
                </div>
              )}

              {/* Assigned Rider */}
              {showRiderCard && (
                <>
                  <StoreOrderAssignedRider
                    riderData={storeOrder?.order?.rider}
                  />
                  <CardSpacer />
                </>
              )}

              {/* Associated Parcel ID */}
              <StoreOrderParcelID
                storeOrder={storeOrder}
                userType={userType}
                onSubmitParcelNo={onSubmitParcelNo}
                onSubmitRunnerScan={onSubmitRunnerScan}
                onSubmitOpsScan={onSubmitOpsScan}
                disableAction={disableAction}
                enableManageMallActions={enableManageMallActions}
              />
              <CardSpacer />
            </div>
          )}
        </div>
      </Grid>
      <SaveButtonBar
        status={null}
        onCancel={onBack}
        onSave={handleCheckAction}
        state={null}
        hideSave={handleHideSaveButton()}
        disabled={disabled || disableAction}
        showTooltip={showTooltip}
        tooltipText="In-store transactions are auto-cancelled after 72 hours. You are required to press completed once customer has picked up the item."
        labels={{
          save:
            storeOrder &&
            handleLabelButton(storeOrder?.smopStatus, shippingMethod, intl)
        }}
      />
    </Container>
  );
};
StoreOrderDetailsPage.displayName = "StoreOrderDetailsPage";
export default StoreOrderDetailsPage;
